/**
 * Last gateway Bearer + refresh token published by whichever tab rotated them.
 * Other tabs adopt this instead of calling /connect/refresh with a refresh token
 * that has already been used.
 */
const STORAGE_KEY = 'rp.gatewayAuth.v1';

export type SharedGatewayAuth = {
  userId?: string;
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresInSeconds?: number;
  expMs: number;
  updatedAt: number;
};

export function readSharedGatewayAuth(): SharedGatewayAuth | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SharedGatewayAuth;
    if (!parsed?.accessToken || !parsed?.refreshToken || typeof parsed.expMs !== 'number') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function publishSharedGatewayAuth(auth: Omit<SharedGatewayAuth, 'updatedAt'>): void {
  const existing = readSharedGatewayAuth();
  if (
    existing &&
    existing.accessToken === auth.accessToken &&
    existing.refreshToken === auth.refreshToken
  ) {
    return;
  }
  const record: SharedGatewayAuth = { ...auth, updatedAt: Date.now() };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    /* ignore quota / private mode */
  }
}

export function subscribeSharedGatewayAuth(
  listener: (auth: SharedGatewayAuth) => void,
): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY || !event.newValue) return;
    const auth = readSharedGatewayAuth();
    if (auth) listener(auth);
  };
  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
}
